import { z } from 'zod';
import { boardSchema } from './board';
import { boardFilterQuerySchema } from './board-filter';
import { cardSchema } from './card';
import { cardListRefSchema } from './card-detail';
import { ulidSchema } from './id';
import { WORKSPACE_NAME_MAX_LENGTH } from './workspace';

/**
 * My Cards — B43's backend contract for `FR-NOTIF-026`.
 *
 * The route lists board cards where the current user has a `card_members` row.
 * Inbox cards are excluded: they have no board/list context and are served by
 * `./inbox` instead.
 */

export const MY_CARDS_SORTS = ['due', 'board'] as const;

export const myCardsSortSchema = z.enum(MY_CARDS_SORTS);

export type MyCardsSort = z.infer<typeof myCardsSortSchema>;

/**
 * `GET /me/cards` querystring.
 *
 * The default excludes archived cards and cards on closed boards. There are no
 * include toggles in B43; adding those is a later route decision. The filter
 * keys reuse the board-filter wire vocabulary where it remains meaningful
 * across boards, but `members` is deliberately absent because this route is
 * already scoped to the caller's card memberships.
 */
export const myCardsQuerySchema = boardFilterQuerySchema
  .omit({ members: true, labels: true })
  .extend({
    workspaceId: ulidSchema
      .optional()
      .describe(
        'Restrict the list to cards on boards in this workspace. Omit for all ' +
          'workspaces the caller may read.',
      ),
    sort: myCardsSortSchema
      .default('due')
      .describe(
        'due sorts dated cards soonest first with undated cards last; board ' +
          'groups the route output by board/list ordering.',
      ),
  });

export type MyCardsQuery = z.infer<typeof myCardsQuerySchema>;

export const myCardsWorkspaceRefSchema = z.object({
  id: ulidSchema,
  name: z.string().max(WORKSPACE_NAME_MAX_LENGTH),
});

export type MyCardsWorkspaceRef = z.infer<typeof myCardsWorkspaceRefSchema>;

export const myCardsBoardRefSchema = boardSchema.extend({
  path: z.string().min(1),
  workspace: myCardsWorkspaceRefSchema,
});

export type MyCardsBoardRef = z.infer<typeof myCardsBoardRefSchema>;

/**
 * One row of `GET /me/cards`.
 *
 * The base board-card contract is extended on a copy. The added fields are the
 * personal surface's context: where the card lives and the date/completion
 * state the page sorts and scans by.
 */
export const myCardsCardSchema = cardSchema.extend({
  board: myCardsBoardRefSchema,
  list: cardListRefSchema,
  startAt: z.string().min(1).nullable(),
  dueAt: z.string().min(1).nullable(),
  /** `cards.due_all_day` — FR-EXP-029 edge case (BE-44.7). */
  dueAllDay: z.boolean(),
  completed: z.boolean(),
});

export type MyCardsCard = z.infer<typeof myCardsCardSchema>;

export const myCardsSchema = z.object({
  cards: z.array(myCardsCardSchema),
});

export type MyCards = z.infer<typeof myCardsSchema>;
